import React, { useState, useEffect } from 'react';
import ConfigurationWrapper from '../../../components/ConfigurationPrice/ConfigurationWrapper';
import { useGetPricing, usePostPricing, useGetPurchaseStep, usePostPurchaseStep } from '../hooks/api/configurationApi';
import { formattedNumber } from '../../../utils/number';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import useAppContext from '../../../hooks/context/useAppContext';

export const getInitValues = () => {
  return {
    currentDayPeriodBestAsk: {
      from: null,
      to: null,
    },
    priceValidityInMinutes: 0,
    fixMarginMonth1: 0,
    fixMarginMonth2: 0,
    fixMarginMonth3: 0,
    fixMarginMonth4: 0,
    fixMarginMonth5: 0,
    fixMarginMonth6: 0,
    fixMarginMonth7: 0,
    fixMarginQuarter1: 0,
    fixMarginQuarter2: 0,
    fixMarginQuarter3: 0,
    fixMarginQuarter4: 0,
    fixMarginQuarter5: 0,
    fixMarginYear1: 0,
    fixMarginYear2: 0,
    fixMarginYear3: 0,
    fixMarginYear4: 0,
    floatingMaxMargin: 0,
    isTrading: true,
    currentDayPeriodSettlement: {
      from: null,
      to: null,
    },
    nextDayPeriodSettlement: {
      from: null,
      to: null,
    },
  };
};

export const getPurchaseStepInit = () => {
  return {
    productType: null,
    settlementMaxAmount: 0,
    settlementMinAmount: 0,
    bestAskMaxAmount: 0,
    bestAskMinAmount: 0,
  };
};

export const createPurchaseObject = (data) => {
  const products = ['ANNUAL', 'QUARTAL', 'MONTH'];
  const dataArray = [];
  for (const product of products) {
    const skelet = getPurchaseStepInit();
    data
      .filter((object) => object.productType === product)
      .map((item) => {
        skelet.productType = item?.productType;
        if (item?.priceSource.id === 'SETTLEMENT') {
          skelet.settlementMaxAmount = item?.maxAmount;
          skelet.settlementMinAmount = item?.minAmount;
        } else if (item?.priceSource.id === 'BESTASK') {
          skelet.bestAskMaxAmount = item?.maxAmount;
          skelet.bestAskMinAmount = item?.minAmount;
        }
        return null;
      });
    dataArray.push(skelet);
  }
  return dataArray;
};

export const createPricingObject = (skelet, data, commodityType) => {
  data
    .filter((object) => object.commodityType === commodityType)
    .map((item) => {
      if (item.priceSource === 'BESTASK') {
        skelet.currentDayPeriodBestAsk.from = item?.currentDayPeriod?.from;
        skelet.currentDayPeriodBestAsk.to = item?.currentDayPeriod?.to;
        skelet.priceValidityInMinutes = item?.priceValidityInMinutes;
        skelet.fixMarginMonth1 = item?.fixMarginMonth1;
        skelet.fixMarginMonth2 = item?.fixMarginMonth2;
        skelet.fixMarginMonth3 = item?.fixMarginMonth3;
        skelet.fixMarginMonth4 = item?.fixMarginMonth4;
        skelet.fixMarginMonth5 = item?.fixMarginMonth5;
        skelet.fixMarginMonth6 = item?.fixMarginMonth6;
        skelet.fixMarginMonth7 = item?.fixMarginMonth7;
        skelet.fixMarginQuarter1 = item?.fixMarginQuarter1;
        skelet.fixMarginQuarter2 = item?.fixMarginQuarter2;
        skelet.fixMarginQuarter3 = item?.fixMarginQuarter3;
        skelet.fixMarginQuarter4 = item?.fixMarginQuarter4;
        skelet.fixMarginQuarter5 = item?.fixMarginQuarter5;
        skelet.fixMarginYear1 = item?.fixMarginYear1;
        skelet.fixMarginYear2 = item?.fixMarginYear2;
        skelet.fixMarginYear3 = item?.fixMarginYear3;
        skelet.fixMarginYear4 = item?.fixMarginYear4;
        skelet.floatingMaxMargin = item?.floatingMaxMargin;
        skelet.isTrading = item?.isTrading;
      }
      if (item.priceSource === 'SETTLEMENT') {
        skelet.currentDayPeriodSettlement.from = item?.currentDayPeriod?.from;
        skelet.currentDayPeriodSettlement.to = item?.currentDayPeriod?.to;
        skelet.nextDayPeriodSettlement.from = item?.nextDayPeriod?.from;
        skelet.nextDayPeriodSettlement.to = item?.nextDayPeriod?.to;
      }
      return null;
    });
  return skelet;
};

export const setPurschaseStepObject = (data, object) => {
  const periods = ['ANNUAL', 'QUARTAL', 'MONTH'];
  data.map((item) => {
    for (const period of periods) {
      if (item?.productType === period) {
        if (item.priceSource.id === 'BESTASK') {
          item.maxAmount = object.filter((o) => o.productType === period).map((item) => formattedNumber(item.bestAskMaxAmount, 3))[0];
          item.minAmount = object.filter((o) => o.productType === period).map((item) => formattedNumber(item.bestAskMinAmount, 3))[0];
        } else if (item.priceSource.id === 'SETTLEMENT') {
          item.maxAmount = object.filter((o) => o.productType === period).map((item) => formattedNumber(item.settlementMaxAmount, 3))[0];
          item.minAmount = object.filter((o) => o.productType === period).map((item) => formattedNumber(item.settlementMinAmount, 3))[0];
        }
      }
    }
    return null;
  });
  return data;
};

const TradingConfig = () => {
  const { getPricing, data, loading, error } = useGetPricing();

  const { getPurchaseStep: getPurchaseStepEle, data: purchaseDataEle, loading: purchaseEleLoading, error: purchaseEleError } = useGetPurchaseStep();
  const { getPurchaseStep: getPurchaseStepGas, data: purchaseDataGas, loading: purchaseGasLoading, error: purchaseGasError } = useGetPurchaseStep();

  const { postPricing, data: postDataPricing, loading: postLoadingPricing, error: postErrorPricing } = usePostPricing();
  const { postPurchaseStep, data: postDataPurchase, loading: postLoadingPurchase, error: postErrorPurchase } = usePostPurchaseStep();

  const [eletricCommodity, setEletricCommodity] = useState(null);
  const [gasTheCommodity, setGasTheCommodity] = useState(null);
  const [gasCzvtpCommodity, setGasCzvtpCommodity] = useState(null);
  const [purschaseStepEle, setPurschaseStepEle] = useState(null);
  const [purschaseStepGas, setPurschaseStepGas] = useState(null);

  const { setBreadCrumbs } = useAppContext();
  const intl = useIntl();

  const settingsTitle = intl.formatMessage({ id: 'app.userMenu.applicationSettings' });

  useEffect(() => {
    setBreadCrumbs(`${settingsTitle}`);
  }, [setBreadCrumbs, settingsTitle]);

  const pricing = {
    eletricCommodity,
    gasTheCommodity,
    gasCzvtpCommodity,
    postDataPricing,
    postDataPurchase,
  };

  const errors = {
    error,
    purchaseEleError,
    purchaseGasError,
    postErrorPricing,
    postErrorPurchase,
  };

  const loaders = {
    loading,
    purchaseEleLoading,
    purchaseGasLoading,
    postLoadingPricing,
    postLoadingPurchase,
  };

  const sendHandler = () => {
    data.map((item) => {
      if (item?.commodityType === 'ELE') {
        if (item.priceSource === 'BESTASK') {
          item.currentDayPeriod.from = eletricCommodity.currentDayPeriodBestAsk.from;
          item.currentDayPeriod.to = eletricCommodity.currentDayPeriodBestAsk.to;
          item.priceValidityInMinutes = formattedNumber(eletricCommodity.priceValidityInMinutes);
          item.fixMarginMonth1 = formattedNumber(eletricCommodity.fixMarginMonth1, 3);
          item.fixMarginMonth2 = formattedNumber(eletricCommodity.fixMarginMonth2, 3);
          item.fixMarginMonth3 = formattedNumber(eletricCommodity.fixMarginMonth3, 3);
          item.fixMarginMonth4 = formattedNumber(eletricCommodity.fixMarginMonth4, 3);
          item.fixMarginMonth5 = formattedNumber(eletricCommodity.fixMarginMonth5, 3);
          item.fixMarginMonth6 = formattedNumber(eletricCommodity.fixMarginMonth6, 3);
          item.fixMarginMonth7 = formattedNumber(eletricCommodity.fixMarginMonth7, 3);
          item.fixMarginQuarter1 = formattedNumber(eletricCommodity.fixMarginQuarter1, 3);
          item.fixMarginQuarter2 = formattedNumber(eletricCommodity.fixMarginQuarter2, 3);
          item.fixMarginQuarter3 = formattedNumber(eletricCommodity.fixMarginQuarter3, 3);
          item.fixMarginQuarter4 = formattedNumber(eletricCommodity.fixMarginQuarter4, 3);
          item.fixMarginQuarter5 = formattedNumber(eletricCommodity.fixMarginQuarter5, 3);
          item.fixMarginYear1 = formattedNumber(eletricCommodity.fixMarginYear1, 3);
          item.fixMarginYear2 = formattedNumber(eletricCommodity.fixMarginYear2, 3);
          item.fixMarginYear3 = formattedNumber(eletricCommodity.fixMarginYear3, 3);
          item.fixMarginYear4 = formattedNumber(eletricCommodity.fixMarginYear4, 3);
          item.floatingMaxMargin = formattedNumber(eletricCommodity.floatingMaxMargin, 3);
          item.isTrading = eletricCommodity.isTrading;
        }
        if (item.priceSource === 'SETTLEMENT') {
          item.currentDayPeriod.from = eletricCommodity.currentDayPeriodSettlement.from;
          item.currentDayPeriod.to = eletricCommodity.currentDayPeriodSettlement.to;
          item.nextDayPeriod.from = eletricCommodity.nextDayPeriodSettlement.from;
          item.nextDayPeriod.to = eletricCommodity.nextDayPeriodSettlement.to;
          item.priceValidityInMinutes = null;
          item.fixMarginMonth1 = null;
          item.fixMarginMonth2 = null;
          item.fixMarginMonth3 = null;
          item.fixMarginMonth4 = null;
          item.fixMarginMonth5 = null;
          item.fixMarginMonth6 = null;
          item.fixMarginMonth7 = null;
          item.fixMarginQuarter1 = null;
          item.fixMarginQuarter2 = null;
          item.fixMarginQuarter3 = null;
          item.fixMarginQuarter4 = null;
          item.fixMarginQuarter5 = null;
          item.fixMarginYear1 = null;
          item.fixMarginYear2 = null;
          item.fixMarginYear3 = null;
          item.fixMarginYear4 = null;
          item.floatingMaxMargin = null;
        }
      } else if (item?.commodityType === 'GAS_THE') {
        if (item.priceSource === 'BESTASK') {
          item.currentDayPeriod.from = gasTheCommodity.currentDayPeriodBestAsk.from;
          item.currentDayPeriod.to = gasTheCommodity.currentDayPeriodBestAsk.to;
          item.priceValidityInMinutes = formattedNumber(gasTheCommodity.priceValidityInMinutes);
          item.fixMarginMonth1 = formattedNumber(gasTheCommodity.fixMarginMonth1, 3);
          item.fixMarginMonth2 = formattedNumber(gasTheCommodity.fixMarginMonth2, 3);
          item.fixMarginMonth3 = formattedNumber(gasTheCommodity.fixMarginMonth3, 3);
          item.fixMarginMonth4 = formattedNumber(gasTheCommodity.fixMarginMonth4, 3);
          item.fixMarginMonth5 = formattedNumber(gasTheCommodity.fixMarginMonth5, 3);
          item.fixMarginMonth6 = formattedNumber(gasTheCommodity.fixMarginMonth6, 3);
          item.fixMarginMonth7 = formattedNumber(gasTheCommodity.fixMarginMonth7, 3);
          item.fixMarginQuarter1 = formattedNumber(gasTheCommodity.fixMarginQuarter1, 3);
          item.fixMarginQuarter2 = formattedNumber(gasTheCommodity.fixMarginQuarter2, 3);
          item.fixMarginQuarter3 = formattedNumber(gasTheCommodity.fixMarginQuarter3, 3);
          item.fixMarginQuarter4 = formattedNumber(gasTheCommodity.fixMarginQuarter4, 3);
          item.fixMarginQuarter5 = formattedNumber(gasTheCommodity.fixMarginQuarter5, 3);
          item.fixMarginYear1 = formattedNumber(gasTheCommodity.fixMarginYear1, 3);
          item.fixMarginYear2 = formattedNumber(gasTheCommodity.fixMarginYear2, 3);
          item.fixMarginYear3 = formattedNumber(gasTheCommodity.fixMarginYear3, 3);
          item.fixMarginYear4 = formattedNumber(gasTheCommodity.fixMarginYear4, 3);
          item.floatingMaxMargin = formattedNumber(gasTheCommodity.floatingMaxMargin, 3);
          item.isTrading = gasTheCommodity.isTrading;
        }
        if (item.priceSource === 'SETTLEMENT') {
          item.currentDayPeriod.from = gasTheCommodity.currentDayPeriodSettlement.from;
          item.currentDayPeriod.to = gasTheCommodity.currentDayPeriodSettlement.to;
          item.nextDayPeriod.from = gasTheCommodity.nextDayPeriodSettlement.from;
          item.nextDayPeriod.to = gasTheCommodity.nextDayPeriodSettlement.to;
          item.priceValidityInMinutes = null;
          item.fixMarginMonth1 = null;
          item.fixMarginMonth2 = null;
          item.fixMarginMonth3 = null;
          item.fixMarginMonth4 = null;
          item.fixMarginMonth5 = null;
          item.fixMarginMonth6 = null;
          item.fixMarginMonth7 = null;
          item.fixMarginQuarter1 = null;
          item.fixMarginQuarter2 = null;
          item.fixMarginQuarter3 = null;
          item.fixMarginQuarter4 = null;
          item.fixMarginQuarter5 = null;
          item.fixMarginYear1 = null;
          item.fixMarginYear2 = null;
          item.fixMarginYear3 = null;
          item.fixMarginYear4 = null;
          item.floatingMaxMargin = null;
        }
      } else if (item?.commodityType === 'GAS_CZVTP') {
        if (item.priceSource === 'BESTASK') {
          item.currentDayPeriod.from = gasCzvtpCommodity.currentDayPeriodBestAsk.from;
          item.currentDayPeriod.to = gasCzvtpCommodity.currentDayPeriodBestAsk.to;
          item.priceValidityInMinutes = formattedNumber(gasCzvtpCommodity.priceValidityInMinutes);
          item.fixMarginMonth1 = formattedNumber(gasCzvtpCommodity.fixMarginMonth1, 3);
          item.fixMarginMonth2 = formattedNumber(gasCzvtpCommodity.fixMarginMonth2, 3);
          item.fixMarginMonth3 = formattedNumber(gasCzvtpCommodity.fixMarginMonth3, 3);
          item.fixMarginMonth4 = formattedNumber(gasCzvtpCommodity.fixMarginMonth4, 3);
          item.fixMarginMonth5 = formattedNumber(gasCzvtpCommodity.fixMarginMonth5, 3);
          item.fixMarginMonth6 = formattedNumber(gasCzvtpCommodity.fixMarginMonth6, 3);
          item.fixMarginMonth7 = formattedNumber(gasCzvtpCommodity.fixMarginMonth7, 3);
          item.fixMarginQuarter1 = formattedNumber(gasCzvtpCommodity.fixMarginQuarter1, 3);
          item.fixMarginQuarter2 = formattedNumber(gasCzvtpCommodity.fixMarginQuarter2, 3);
          item.fixMarginQuarter3 = formattedNumber(gasCzvtpCommodity.fixMarginQuarter3, 3);
          item.fixMarginQuarter4 = formattedNumber(gasCzvtpCommodity.fixMarginQuarter4, 3);
          item.fixMarginQuarter5 = formattedNumber(gasCzvtpCommodity.fixMarginQuarter5, 3);
          item.fixMarginYear1 = formattedNumber(gasCzvtpCommodity.fixMarginYear1, 3);
          item.fixMarginYear2 = formattedNumber(gasCzvtpCommodity.fixMarginYear2, 3);
          item.fixMarginYear3 = formattedNumber(gasCzvtpCommodity.fixMarginYear3, 3);
          item.fixMarginYear4 = formattedNumber(gasCzvtpCommodity.fixMarginYear4, 3);
          item.floatingMaxMargin = formattedNumber(gasCzvtpCommodity.floatingMaxMargin, 3);
          item.isTrading = gasCzvtpCommodity.isTrading;
        }
        if (item.priceSource === 'SETTLEMENT') {
          item.currentDayPeriod.from = gasCzvtpCommodity.currentDayPeriodSettlement.from;
          item.currentDayPeriod.to = gasCzvtpCommodity.currentDayPeriodSettlement.to;
          item.nextDayPeriod.from = gasCzvtpCommodity.nextDayPeriodSettlement.from;
          item.nextDayPeriod.to = gasCzvtpCommodity.nextDayPeriodSettlement.to;
          item.priceValidityInMinutes = null;
          item.fixMarginMonth1 = null;
          item.fixMarginMonth2 = null;
          item.fixMarginMonth3 = null;
          item.fixMarginMonth4 = null;
          item.fixMarginMonth5 = null;
          item.fixMarginMonth6 = null;
          item.fixMarginMonth7 = null;
          item.fixMarginQuarter1 = null;
          item.fixMarginQuarter2 = null;
          item.fixMarginQuarter3 = null;
          item.fixMarginQuarter4 = null;
          item.fixMarginQuarter5 = null;
          item.fixMarginYear1 = null;
          item.fixMarginYear2 = null;
          item.fixMarginYear3 = null;
          item.fixMarginYear4 = null;
          item.floatingMaxMargin = null;
        }
      }
      return null;
    });
    postPricing(data);
  };

  const sendHandlerPurchase = (type) => {
    let body = {};
    if (purchaseDataEle) {
      if (type === 'ELE') {
        body = setPurschaseStepObject(purchaseDataEle, purschaseStepEle);
      } else if (type === 'GAS') {
        body = setPurschaseStepObject(purchaseDataGas, purschaseStepGas);
      }
    }
    if (Object.keys(body).length !== 0) {
      postPurchaseStep(type, body);
    }
  };

  const handlers = {
    setEletricCommodity,
    setGasTheCommodity,
    setGasCzvtpCommodity,
    sendHandler,
    setPurschaseStepEle,
    setPurschaseStepGas,
    sendHandlerPurchase,
  };

  useEffect(() => {
    if (data) {
      setEletricCommodity(createPricingObject(getInitValues(), data, 'ELE'));
      setGasTheCommodity(createPricingObject(getInitValues(), data, 'GAS_THE'));
      setGasCzvtpCommodity(createPricingObject(getInitValues(), data, 'GAS_CZVTP'));
    }
  }, [data]);

  useEffect(() => {
    (async () => {
      await getPricing();
    })();
  }, [getPricing]);

  useEffect(() => {
    purchaseDataEle && setPurschaseStepEle(createPurchaseObject(purchaseDataEle));
    purchaseDataGas && setPurschaseStepGas(createPurchaseObject(purchaseDataGas));
  }, [purchaseDataEle, purchaseDataGas]);

  useEffect(() => {
    (async () => {
      await getPurchaseStepEle('ELE');
      await getPurchaseStepGas('GAS');
    })();
  }, [getPurchaseStepEle, getPurchaseStepGas]);

  return (
    <>
      <ConfigurationWrapper
        loading={loading}
        error={error}
        pricing={pricing}
        purschaseStepEle={purschaseStepEle}
        purschaseStepGas={purschaseStepGas}
        handlers={handlers}
        errors={errors}
        loaders={loaders}
      />
    </>
  );
};

TradingConfig.propTypes = {
  id: PropTypes.string,
};
export default TradingConfig;
